import { useCallback, useEffect, useState } from "react"
import { toast } from "react-toastify"
import clsx from "clsx"

import { useTranslation } from "react-i18next"

import { LoadingIndicator } from "components/general/LoadingIndicator"
import { TablePagination } from "components/paginations/TablePagination"

import { usePermissions } from "contexts/PermissionsContext"
import { useProps } from "contexts/PropsContext"

import { PERMISSIONS } from "constants/permissions"

import { AdminTokenService } from "services/token/AdminTokenService"
import { TokenConfig, TokenConfigChange, TokenConfigPatch } from "services/token/type"

import { compareIntegerStrings, formatBps, formatDateTime, formatDecimalString, formatUsdCents, isIntegerString, isPositiveDecimal, tokenErrorCopy, Translate } from "../helpers/token.helpers"
import { useTokenGate } from "../helpers/useTokenGate"

import { ReasonModal } from "./shared/ReasonModal"
import { TokenField, TokenReadout } from "./shared/TokenField"
import { TokenUnavailable } from "./shared/TokenUnavailable"

const CHANGES_PAGE_SIZE = 10

/** Draft state mirrors the wire format: strings in, strings out, no Number(). */
interface Draft {
  price_usd: string
  sale_paused: boolean
  min_purchase_usd_cents: string
  max_purchase_usd_cents: string
  daily_cap_per_user_usd_cents: string
  platform_fee_bps: string
  token_symbol: string
  token_name: string
}

const toDraft = (c: TokenConfig): Draft => ({
  price_usd: c.price_usd,
  sale_paused: c.sale_paused,
  min_purchase_usd_cents: c.min_purchase_usd_cents,
  max_purchase_usd_cents: c.max_purchase_usd_cents,
  daily_cap_per_user_usd_cents: c.daily_cap_per_user_usd_cents,
  platform_fee_bps: String(c.platform_fee_bps),
  token_symbol: c.token_symbol,
  token_name: c.token_name
})

export const PresaleTab = ({ tenant }: { tenant: string }) => {
  const { t } = useTranslation("translation", { useSuspense: false }) as { t: Translate }
  const { darkMode } = useProps()
  const { has } = usePermissions()
  // PAGE_TOKEN opens this tab; changing the price is a separate privilege. The
  // form stays readable either way — the save path is what closes.
  const canEdit = has(PERMISSIONS.TOKEN_CONFIG_EDIT)

  const { gate, loading, run } = useTokenGate()
  const [config, setConfig] = useState<TokenConfig | null>(null)
  const [draft, setDraft] = useState<Draft | null>(null)

  const [changes, setChanges] = useState<TokenConfigChange[]>([])
  const [changesPage, setChangesPage] = useState(1)
  const [changesPages, setChangesPages] = useState(1)

  const [confirming, setConfirming] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | undefined>()

  const load = useCallback(async () => {
    if (!tenant) return
    const data = await run(() => AdminTokenService.getConfig(tenant))
    if (data) {
      setConfig(data)
      setDraft(toDraft(data))
    }
  }, [tenant, run])

  const loadChanges = useCallback(async () => {
    if (!tenant) return
    const res = await AdminTokenService.listConfigChanges(tenant, changesPage, CHANGES_PAGE_SIZE)
    if (res.ok && res.data) {
      setChanges(res.data.changes)
      setChangesPages(res.data.pagination.pages)
    }
  }, [tenant, changesPage])

  useEffect(() => {
    load()
  }, [load])
  useEffect(() => {
    loadChanges()
  }, [loadChanges])

  if (loading) return <LoadingIndicator text={t("token.presaleLoading")} />
  if (gate !== "ok") return <TokenUnavailable variant={gate} onRetry={load} />
  if (!config || !draft) return <TokenUnavailable variant="unavailable" onRetry={load} />

  // ── Validation ────────────────────────────────────────────────────────────
  // Mirrors token-service, which is the authority. Duplicated here only to fail
  // fast in the form rather than after a round trip — never as the guarantee.
  const priceInvalid = !isPositiveDecimal(draft.price_usd)
  const minInvalid = !isIntegerString(draft.min_purchase_usd_cents) || draft.min_purchase_usd_cents === "0"
  const maxInvalid = !isIntegerString(draft.max_purchase_usd_cents) || draft.max_purchase_usd_cents === "0"
  const capInvalid = !isIntegerString(draft.daily_cap_per_user_usd_cents) || draft.daily_cap_per_user_usd_cents === "0"
  const feeNum = Number(draft.platform_fee_bps)
  // Safe: bps is a small integer, not a money value.
  const feeInvalid = !/^\d+$/.test(draft.platform_fee_bps) || feeNum > 1000
  // Cross-field, on the POST-update values — an admin raising only the minimum
  // must still be stopped when it lands above the existing maximum.
  const rangeInverted = !minInvalid && !maxInvalid && compareIntegerStrings(draft.min_purchase_usd_cents, draft.max_purchase_usd_cents) > 0

  // Mirrors the TokenConfig_token_symbol_format CHECK. The "$" is added by the
  // player UI, so a typed one would render "$$STARO".
  const symbolInvalid = !/^[A-Za-z0-9][A-Za-z0-9._-]{0,11}$/.test(draft.token_symbol.trim())
  const nameInvalid = draft.token_name.trim().length < 1 || draft.token_name.trim().length > 40

  const invalid =
    priceInvalid || minInvalid || maxInvalid || capInvalid || feeInvalid || rangeInverted || symbolInvalid || nameInvalid

  /** Only fields that ACTUALLY differ are sent, matching the server's change log. */
  const patch: TokenConfigPatch = {}
  if (draft.price_usd !== config.price_usd) patch.price_usd = draft.price_usd
  if (draft.sale_paused !== config.sale_paused) patch.sale_paused = draft.sale_paused
  if (draft.min_purchase_usd_cents !== config.min_purchase_usd_cents) patch.min_purchase_usd_cents = draft.min_purchase_usd_cents
  if (draft.max_purchase_usd_cents !== config.max_purchase_usd_cents) patch.max_purchase_usd_cents = draft.max_purchase_usd_cents
  if (draft.daily_cap_per_user_usd_cents !== config.daily_cap_per_user_usd_cents) patch.daily_cap_per_user_usd_cents = draft.daily_cap_per_user_usd_cents
  if (feeNum !== config.platform_fee_bps) patch.platform_fee_bps = feeNum
  if (draft.token_symbol.trim() !== config.token_symbol) patch.token_symbol = draft.token_symbol.trim()
  if (draft.token_name.trim() !== config.token_name) patch.token_name = draft.token_name.trim()

  const dirty = Object.keys(patch).length > 0

  const submit = async (reason: string) => {
    setSubmitting(true)
    setError(undefined)
    const res = await AdminTokenService.updateConfig(tenant, reason, patch)
    setSubmitting(false)

    if (!res.ok) {
      setError(tokenErrorCopy(t, res.code, res.message))
      return
    }

    setConfirming(false)
    toast.success(t("token.savedToast", { fields: res.data?.length ?? 0 }))
    // No React Query: refetch explicitly, both the config and its audit log.
    await load()
    await loadChanges()
  }

  const set = (k: keyof Draft, v: string | boolean) => setDraft({ ...draft, [k]: v })

  return (
    <div className="grid gap-8">
      {/* Fixed at launch — surfaced so nobody hunts for an edit control that
          deliberately does not exist. */}
      <section className="grid gap-4 md:grid-cols-3">
        <TokenReadout label={t("token.totalSupply")} value={formatDecimalString(config.total_supply, 0)} hint={t("token.fixedAtLaunch")} />
        <TokenReadout label={t("token.presaleSupply")} value={formatDecimalString(config.presale_supply, 0)} hint={t("token.fixedAtLaunch")} />
        <TokenReadout
          label={t("token.remainingSupply")}
          value={formatDecimalString(config.remaining_supply, 2)}
          hint={t("token.updatedAt", { when: formatDateTime(config.updated_at) })}
        />
      </section>

      <section className={clsx("rounded-lg border p-6", darkMode ? "border-[rgba(255,255,255,0.1)] bg-[#0C0C1A]" : "border-gray-200 bg-white")}>
        <h3 className="mb-1 text-lg font-semibold">{t("token.identityTitle")}</h3>
        <p className="mb-5 text-sm opacity-70">{t("token.identityBlurb")}</p>

        <div className="grid gap-5 md:grid-cols-2">
          <TokenField
            label={t("token.tickerLabel")}
            value={draft.token_symbol}
            onChange={canEdit ? (v) => set("token_symbol", v) : undefined}
            readOnly={!canEdit}
            invalid={symbolInvalid}
            hint={symbolInvalid ? t("token.tickerInvalid") : t("token.tickerHint", { symbol: draft.token_symbol.trim() || "…" })}
          />

          <TokenField
            label={t("token.displayNameLabel")}
            value={draft.token_name}
            onChange={canEdit ? (v) => set("token_name", v) : undefined}
            readOnly={!canEdit}
            invalid={nameInvalid}
            hint={nameInvalid ? t("token.displayNameInvalid") : t("token.displayNameHint")}
          />
        </div>
      </section>

      <section className={clsx("rounded-lg border p-6", darkMode ? "border-[rgba(255,255,255,0.1)] bg-[#0C0C1A]" : "border-gray-200 bg-white")}>
        <h3 className="mb-5 text-lg font-semibold">{t("token.presaleSettings")}</h3>

        {!canEdit && <p className="mb-5 text-sm text-amber-400">{t("token.presaleReadOnly")}</p>}

        <div className="grid gap-5 md:grid-cols-2">
          <TokenField
            label={t("token.priceLabel", { symbol: config.token_symbol })}
            value={draft.price_usd}
            onChange={canEdit ? (v) => set("price_usd", v) : undefined}
            readOnly={!canEdit}
            invalid={priceInvalid}
            hint={priceInvalid ? t("token.priceInvalid") : t("token.priceHint")}
          />

          <TokenField
            label={t("token.platformFeeLabel")}
            value={draft.platform_fee_bps}
            onChange={canEdit ? (v) => set("platform_fee_bps", v) : undefined}
            readOnly={!canEdit}
            invalid={feeInvalid}
            suffix="bps"
            hint={feeInvalid ? t("token.platformFeeInvalid") : t("token.platformFeeHint", { value: formatBps(config.platform_fee_bps) })}
          />

          <TokenField
            label={t("token.minPurchaseLabel")}
            value={draft.min_purchase_usd_cents}
            onChange={canEdit ? (v) => set("min_purchase_usd_cents", v) : undefined}
            readOnly={!canEdit}
            invalid={minInvalid || rangeInverted}
            suffix="cents"
            hint={rangeInverted ? t("token.rangeInverted") : formatUsdCents(draft.min_purchase_usd_cents)}
          />

          <TokenField
            label={t("token.maxPurchaseLabel")}
            value={draft.max_purchase_usd_cents}
            onChange={canEdit ? (v) => set("max_purchase_usd_cents", v) : undefined}
            readOnly={!canEdit}
            invalid={maxInvalid || rangeInverted}
            suffix="cents"
            hint={formatUsdCents(draft.max_purchase_usd_cents)}
          />

          <TokenField
            label={t("token.dailyCapLabel")}
            value={draft.daily_cap_per_user_usd_cents}
            onChange={canEdit ? (v) => set("daily_cap_per_user_usd_cents", v) : undefined}
            readOnly={!canEdit}
            invalid={capInvalid}
            suffix="cents"
            hint={formatUsdCents(draft.daily_cap_per_user_usd_cents)}
          />

          <div className="flex items-end">
            <label className="flex cursor-pointer items-center gap-3">
              <input
                type="checkbox"
                checked={draft.sale_paused}
                disabled={!canEdit}
                onChange={() => set("sale_paused", !draft.sale_paused)}
                className="h-5 w-5 accent-[rgba(132,0,168,1)]"
              />
              <span>
                <span className="block text-sm font-medium">{t("token.pauseSale")}</span>
                <span className={clsx("block text-xs", darkMode ? "text-gray-500" : "text-gray-500")}>{t("token.pauseSaleHint")}</span>
              </span>
            </label>
          </div>
        </div>

        <div className="mt-6 flex items-center gap-4">
          <button
            type="button"
            disabled={!canEdit || !dirty || invalid}
            onClick={() => {
              setError(undefined)
              setConfirming(true)
            }}
            className="rounded-lg bg-[rgba(132,0,168,1)] px-6 py-2 text-white transition-all hover:bg-[rgba(169,78,206,1)] disabled:cursor-not-allowed disabled:opacity-40"
          >
            {t("token.saveChanges")}
          </button>
          {dirty && !invalid && (
            <span className={clsx("text-sm", darkMode ? "text-gray-400" : "text-gray-600")}>
              {t("token.fieldsWillChange", { fields: Object.keys(patch).length })}
            </span>
          )}
          {!dirty && <span className={clsx("text-sm", darkMode ? "text-gray-500" : "text-gray-500")}>{t("token.noChanges")}</span>}
        </div>
      </section>

      <section className={clsx("rounded-lg border p-6", darkMode ? "border-[rgba(255,255,255,0.1)] bg-[#0C0C1A]" : "border-gray-200 bg-white")}>
        <h3 className="mb-1 text-lg font-semibold">{t("token.changeLog")}</h3>
        <p className={clsx("mb-5 text-sm", darkMode ? "text-gray-400" : "text-gray-600")}>
          {t("token.changeLogBlurb")}
        </p>

        {changes.length === 0 ? (
          <p className={clsx("py-8 text-center text-sm", darkMode ? "text-gray-500" : "text-gray-500")}>{t("token.changeLogEmpty")}</p>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className={clsx("text-left", darkMode ? "text-gray-400" : "text-gray-600")}>
                    <th className="pb-3 pr-4 font-medium">{t("token.colWhen")}</th>
                    <th className="pb-3 pr-4 font-medium">{t("token.colField")}</th>
                    <th className="pb-3 pr-4 font-medium">{t("token.colBefore")}</th>
                    <th className="pb-3 pr-4 font-medium">{t("token.colAfter")}</th>
                    <th className="pb-3 pr-4 font-medium">{t("token.colAdmin")}</th>
                    <th className="pb-3 font-medium">{t("token.colReason")}</th>
                  </tr>
                </thead>
                <tbody>
                  {changes.map((c, i) => (
                    <tr key={`${c.created_at}-${c.field}-${i}`} className={clsx("border-t", darkMode ? "border-[rgba(255,255,255,0.06)]" : "border-gray-100")}>
                      <td className="whitespace-nowrap py-3 pr-4">{formatDateTime(c.created_at)}</td>
                      <td className="py-3 pr-4 font-mono text-xs">{c.field}</td>
                      <td className="py-3 pr-4 font-mono text-xs text-red-400">{c.old_value}</td>
                      <td className="py-3 pr-4 font-mono text-xs text-green-400">{c.new_value}</td>
                      <td className="py-3 pr-4 font-mono text-xs">{c.admin_id}</td>
                      <td className="py-3">{c.reason || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {changesPages > 1 && <TablePagination page={changesPage} totalPages={changesPages} onJump={setChangesPage} />}
          </>
        )}
      </section>

      <ReasonModal
        open={confirming}
        title={t("token.savePresaleTitle")}
        confirmLabel={t("token.save")}
        submitting={submitting}
        error={error}
        onCancel={() => setConfirming(false)}
        onConfirm={submit}
        description={
          <>
            <p>{t("token.fieldsWillChangeIntro")}</p>
            <ul className="mt-2 list-inside list-disc font-mono text-xs">
              {Object.keys(patch).map((f) => (
                <li key={f}>{f}</li>
              ))}
            </ul>
            {patch.price_usd && <p className="mt-3 text-amber-400">{t("token.priceBroadcastWarning")}</p>}
          </>
        }
      />
    </div>
  )
}
